import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Button, Container, Spinner, InputGroup } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { DataContext } from '../../adapter';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    PlanGetService,
    TransactionStatusService,
    PolicyCreateServices,
    GetAddressServices,
    GetNationalityService
} from '../../domain';
import Swal from 'sweetalert2';
import { modalSuccessClaimsCreateComponent, confirmationModalComponent } from '../components';
import Select from 'react-select';

const { sourceOfFunds, employerBusinessNature } = require('../../services/data/mapping');

const StyledContainer = styled(Container)`
    font-family: Montserrat, sans-serif;
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1);
    padding: 2% 15% !important;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 2% 10%;
    }
`;

const StyledContainer2 = styled(Container)`
    padding: 0 5%;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 0;
    }
`;

const Title = styled.h1`
    font-size: 42px;
    font-weight: 700;
    text-align: left;
`;

const Description = styled.p`
    font-size: 17px;
    font-weight: 400;
    color: black;
`;

const StyledForm = styled(Form)`
    .form-group-label {
        font-size: 16px;
        font-weight: 500;
    }
`;

const StyledSubmitButton = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 40px;
    border-radius: 25px;
    font-family: Montserrat, sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 20px;
        text-align: center;
    }
`;

const StyledSubmitButton2 = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 30px;
    border-radius: 25px;
    font-family: Montserrat, sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center !important;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 40px;
        margin-bottom: 10px;
        text-align: center;
    }
`;

const Loading = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
`;

const RequiredTag = styled.span`
    color: #B01116;
`;

const CenteredRow = styled(Row)`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

export default function Kyc() {
    const { claimsData, updateClaimsData, clearClaimsData } = DataContext();
    const [agreed, setAgreed] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [formDataError, setFormDataError] = useState(null);
    const [fundsOptions, setFundsOptions] = useState([]);
    const [businessNatureOptions, setBusinessNatureOptions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [address, setAddress] = useState({});
    const [nationality, setNationality] = useState([]);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isNationalityDisabled, setIsNationalityDisabled] = useState(false);
    const navigate = useNavigate();

    const _PlanGetService = new PlanGetService();
    const _PolicyCreateServices = new PolicyCreateServices();
    const _GetAddressServices = new GetAddressServices();
    const _GetNationalityService = new GetNationalityService();

    const handleCheckboxChange = (e) => {
        setAgreed(e.target.checked);
    };

    const validateFormData = (formData) => {
        const fieldsToValidate = [
            { field: 'nationality', message: 'Nationality Required.' },
            { field: 'dob', message: 'Birthdate is Required.', pastDateMessage: 'Birthdate is invalid.' },
            { field: 'birthCity', message: 'City of Birth is Required.' },
            { field: 'tin', message: 'TIN is required and must be 14 digits.', regex: /^\d{14}$/ },
            { field: 'gender', message: 'Gender is Required.' },
            { field: 'occupationClass', message: 'Nature of Work is Required.' },
            { field: 'empBusName', message: 'Name of Employer is Required.' },
            { field: 'empBusNature', message: 'Nature of Self-Employment/Business is Required.' },
            { field: 'sourceFund', message: 'Source of Funds is Required.' },
            { field: 'street', message: 'Street is Required.' },
            { field: 'barangay', message: 'Barangay is Required.' },
            { field: 'city', message: 'City is Required.' },
            { field: 'province', message: 'Province is Required.' },
            { field: 'zipCode', message: 'Zip Code is Required.' }
        ];

        let errors = [];

        fieldsToValidate.forEach(({ field, message, regex, pastDateMessage }) => {
            if (field === 'dob') {
                const dateOfBirth = new Date(formData[field]);
                const today = new Date();

                if (!formData[field]) {
                    errors.push({ item: field, message: message });
                } else if (dateOfBirth >= today) {
                    errors.push({ item: field, message: pastDateMessage });
                }
            } else if (field === 'tin') {
                // Remove dashes for validation
                const tinValue = formData[field]?.replace(/-/g, ''); // Remove dashes for validation

                if (!tinValue || (regex && !regex.test(tinValue))) {
                    errors.push({ item: field, message });
                }
            } else if (!formData[field] || (regex && !regex.test(formData[field]))) {
                errors.push({ item: field, message });
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
    
        if (name === 'tin') {
            // Remove all non-numeric characters
            let numericValue = value.replace(/\D/g, '');
    
            // Allow backspace when deleting values
            if (e.nativeEvent.inputType === 'deleteContentBackward') {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: numericValue // Just update without formatting
                }));
                return;
            }
    
            // Limit the numeric value to 14 characters
            numericValue = numericValue.slice(0, 14);
    
            // Update the state with the numeric value
            setFormData((prevState) => ({
                ...prevState,
                [name]: numericValue
            }));
        } else {
            // Handle other fields
            setFormData((prevState) => ({
                ...prevState,
                [name]: value
            }));
        }
    };
    
    const handleNationality = async () => {
        try {
            setLoading(true);
            const _nationality = await _GetNationalityService.execute();
            console.log('_nationality', _nationality);
            const { countries } = _nationality;
            console.log('countries', countries);
            const mappedNationality = countries.map((item) => ({
                ...item,
                key: item.countryCd,
                value: item.countryName,
                label: item.countryName
            }));

            const sortedNationality = mappedNationality.sort((a, b) => a.label.localeCompare(b.label));

            setNationality(sortedNationality);
        } catch (error) {
            console.error('Error fetching nationality:', error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const handleAddress = async () => {
        try {
            setLoading(true);
            const _address = await _GetAddressServices.execute();

            const { province, city, barangay } = _address;
            // console.log('address', _address);

            const mappedProvince = province.map((item) => {
                return { ...item, key: item.provinceName, value: item.provinceName };
            });

            const mappedCity = city.map((item) => {
                return { ...item, key: item.cityName, value: item.cityName };
            });

            const mappedBarangay = barangay.map((item) => {
                return { ...item, key: item.postalDesc, value: item.postalDesc };
            });

            setAddress({
                province: mappedProvince,
                city: mappedCity,
                barangay: mappedBarangay
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching address:', error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const handlePlan = async () => {
        try {
            setLoading(true);
            const response = await _PlanGetService.execute();
            const plan = response.plan[0];
            // console.log('plan', plan);
            setPlans(plan);

            setLoading(false);
        } catch (error) {
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            console.error('Error:', error);
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormDataError(null); // Clear any previous errors
        const { isValid, errors } = validateFormData(formData);

        if (!agreed) {
            Swal.fire({
                text: 'You must agree to the terms and conditions to proceed.',
                icon: 'error',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            return;
        }

        // Remove dashes before submission
        const formattedTIN = formData?.tin?.replace(/-/g, ''); // Remove dashes from the TIN

        setFormData({ ...formData, tin: formattedTIN, formSubmitted: true });
        if (!isValid) {
            setFormDataError(errors);
            console.log(errors);
            return;
        }

        setFormData({ ...formData, formSubmitted: true });
        if (!isValid) {
            setFormDataError(errors);
            console.log(errors);
            return;
        }

        confirmationModalComponent({ handleClientSelection, handlePlan, plan: plans, formData, claimsData });
    };

    const handleClientSelection = async () => {
        const policy = claimsData.policy?.policy;
        const planId = plans ? plans.id : null;
        const { firstName, middleName, lastName, street, barangay, city, province, zipCode } = formData;
        const formattedTin = formData.tin.replace(/-/g, '');
        const name = `${firstName}${middleName ? ' ' + middleName + ' ' : ' '}${lastName}`;
        const fullAddress = `${street}, ${barangay}, ${city}, ${province}, ${zipCode}`;
        const claimsPayload = {
            policyType: 'HCR',
            autoRenewTag: 0,
            planId,
            tranId: policy.tranId || null,
            policyUuid: policy.policyUuid || null,
            customer: {
                ...formData,
                fullName: name,
                displayName: name,
                tin: formattedTin,
                address: fullAddress
            }
        };

        console.log('claimsPayload', claimsPayload);
        // return;
        setLoading(true);

        try {
            const response = await _PolicyCreateServices.execute(claimsPayload);
            console.log('responseresponse', response);
            // return
            const { Location } = response;
            console.log('Location', Location);
            if (Location) {
                window.location.href = Location;
            } else {
                Swal.fire({
                    text: 'Sorry. Something went wrong. Please try again later.',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                navigate('/home');
            }
        } catch (error) {
            console.error('Error executing:', error);
            Swal.fire({
                text: 'Sorry. Something went wrong. Please try again later.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
        } finally {
            navigate('/home');
            setLoading(false);
            clearClaimsData();
        }
        return;
        // if (!response.claim) {
        //     Swal.fire({
        //         text: 'Sorry. Something went wrong. Please try again later.',
        //         icon: 'warning',
        //         confirmButtonColor: '#3085d6',
        //         confirmButtonText: 'OK',
        //         customClass: {
        //             popup: 'my-popup',
        //             confirmButton: 'my-confirm-button',
        //             cancelButton: 'my-cancel-button'
        //         }
        //     });
        //     return;
        // }
    };

    const filterCity = (province) => {
        const matchedCity = address.city.filter((item) => item.provinceCd === province.provinceCd);
        handleChange({ target: { name: 'province', value: province.provinceName } });
        handleChange({ target: { name: 'city', value: null } });
        handleChange({ target: { name: 'barangay', value: null } });
        handleChange({ target: { name: 'zipCode', value: '' } });

        setAddress({
            ...address,
            matchedCity: matchedCity,
            matchedBarangay: null
        });
    };

    const filterBarangay = (city) => {
        const matchedBarangay = address.barangay.filter(
            (item) => item.provinceCd === city.provinceCd && item.cityCd === city.cityCd
        );
        handleChange({ target: { name: 'city', value: city.cityName } });
        handleChange({ target: { name: 'barangay', value: null } });
        handleChange({ target: { name: 'zipCode', value: '' } });

        setAddress({
            ...address,
            matchedBarangay: matchedBarangay
        });
    };

    useEffect(() => {
        const policyData = claimsData.policy?.policy;
        console.log('policyData', policyData);
    
        if (policyData) {
            const readOnly = policyData.customerId !== null;
            setIsReadOnly(readOnly);
    
            const populateAddressFields = policyData.customerId !== null;
            
            const formattedDob = policyData.dob ? new Date(policyData.dob).toISOString().split('T')[0] : '';

            const initialNationality = nationality.some(option => option.value === policyData.nationality)
            ? policyData.nationality
            : null;

            const isValidProvince = address.province?.some((option) => option.value === policyData.province)
                ? policyData.province
                : null;
            
            const isValidCity = address.city?.some((option) => option.value === policyData.city)
                ? policyData.city
                : null;
            
            const isValidBarangay = address.barangay?.some((option) => option.value === policyData.barangay)
                ? policyData.barangay
                : null;
            
            
            const province = isValidBarangay ? policyData.province : null;
            const street = isValidBarangay ? policyData.street : null;
            const city = isValidBarangay ? policyData.city : null;
            const zipCode = isValidBarangay ? policyData.zipCode : null;

            setFormData({
                ...policyData,
                dob: formattedDob,
                nationality: initialNationality,
                street: street,
                province: province,
                city: city,
                barangay: isValidBarangay,
                formSubmitted: false,
                zipCode: zipCode
                // readOnlyFields: policyData.customerId === null ? ["firstName", "lastName", "email", "mobileNo"] : []
            });
            setIsNationalityDisabled(!!initialNationality);
        } 
    }, [claimsData, nationality, address.province, address.city, address.barangay]);
    

    useEffect(() => {
        if (formData?.formSubmitted) {
            const { isValid, errors } = validateFormData(formData);
            setFormDataError(errors);
        }
        console.log('formData: ', formData);
    }, [formData]);

    useEffect(() => {
        handlePlan();
        handleAddress();
        handleNationality();
        setFundsOptions(Object.entries(sourceOfFunds).map(([key, value]) => ({ key, value })));

        setBusinessNatureOptions(Object.entries(employerBusinessNature).map(([key, value]) => ({ key, value })));
    }, []);

    return (
        <>
            {loading && (
                <Loading>
                    <Spinner animation="border" variant="light" />
                </Loading>
            )}
            <StyledContainer fluid>
                <Row className="pt-4">
                    <Title>Let's get you covered!</Title>
                </Row>
                <Row>
                    <Description>
                        Punan ang form sa baba para masiguro ang tuloy-tuloy na protekyon sa isang buong taon.
                    </Description>
                </Row>
                {/* <Row>
                        <StyledDescription>
                            Para makumpleto ang iyong claim, pakipunan ang form ng iyong pangunahing impormasyong,
                            source of funds, at ang mga detalye ng iyong employer.
                        </StyledDescription>
                    </Row> */}
                <StyledForm onSubmit={handleSubmit}>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    FIRST NAME<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    // onChange={(e) => {
                                    //     if (!isReadOnly || formData.firstName) {
                                    //         // Only allow change if not read-only
                                    //         setFormData((val) => ({ ...val, firstName: e.target.value }));
                                    //     }
                                    // }}
                                    disabled={isReadOnly || formData.firstName}
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    LAST NAME<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    // onChange={(e) => {
                                    //     if (!isReadOnly || formData.lastName) {
                                    //         // Only allow change if not read-only
                                    //         setFormData((val) => ({ ...val, lastName: e.target.value }));
                                    //     }
                                    // }}
                                    disabled={isReadOnly || formData.lastName} // Set disabled based on customerId
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    NATIONALITY<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="nationality"
                                    id="nationality"
                                    options={nationality}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={nationality.find((option) => option.value === formData.nationality)}
                                    onChange={(option) => {
                                            setFormData((val) => ({
                                                ...val,
                                                nationality: option ? option.countryName : null
                                            }))
                                    }}
                                    placeholder="Select nationality"
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'nationality')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                    isDisabled={isNationalityDisabled}
                                />
                                {formDataError && formDataError.find((item) => item.item === 'nationality') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'nationality')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    BIRTHDATE<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="date"
                                        name="dob"
                                        value={formData.dob}
                                        placeholder="mm/dd/yyyy"
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                // Only allow change if not read-only
                                                setFormData((val) => ({ ...val, dob: e.target.value }));
                                            }
                                        }}
                                        isInvalid={formDataError && !!formDataError.find((item) => item.item === 'dob')}
                                        style={{ zIndex: 0 }}
                                        disabled={isReadOnly}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError && formDataError.find((item) => item.item === 'dob')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    CITY OF BIRTH<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="cob"
                                        value={formData.birthCity}
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                // Only allow change if not read-only
                                                setFormData((val) => ({ ...val, birthCity: e.target.value }));
                                            }
                                        }}
                                        isInvalid={
                                            formDataError && !!formDataError.find((item) => item.item === 'birthCity')
                                        }
                                        disabled={isReadOnly}
                                        style={{ zIndex: 0 }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError &&
                                            formDataError.find((item) => item.item === 'birthCity')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    GENDER<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Select
                                        size="md"
                                        name="gender"
                                        value={formData.gender}
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                // Only allow change if not read-only
                                                setFormData((val) => ({ ...val, gender: e.target.value }));
                                            }
                                        }}
                                        isInvalid={
                                            formDataError && !!formDataError.find((item) => item.item === 'gender')
                                        }
                                        style={{ zIndex: 0 }}
                                        disabled={isReadOnly}
                                    >
                                        <option value="">Choose gender</option>
                                        <option value="m">Male</option>
                                        <option value="f">Female</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError && formDataError.find((item) => item.item === 'gender')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    EMAIL<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((val) => ({ ...val, email: e.target.value }))}
                                    // disabled={!formData?.customerId} // Disable if read-only and no customerId
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    MOBILE NUMBER<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="mobileNo"
                                    value={formData.mobileNo}
                                    onChange={(e) => setFormData((val) => ({ ...val, mobileNo: e.target.value }))}
                                    // disabled={!formData?.customerId} // Disable if read-only and no customerId
                                />
                            </Form.Group>
                        </Col>

                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    TIN<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="tin"
                                        value={formData.tin}
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                handleChange(e);
                                            }
                                        }}
                                        isInvalid={formDataError && !!formDataError.find((item) => item.item === 'tin')}
                                        style={{ zIndex: 0 }}
                                        disabled={isReadOnly} // Make the field read-only based on isReadOnly
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError && formDataError.find((item) => item.item === 'tin')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={7}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    HOUSE NUMBER & STREET NAME<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="hnsn"
                                    maxLength={100}
                                    value={formData.street}
                                    isInvalid={formDataError && !!formDataError.find((item) => item.item === 'street')}
                                    onChange={(e) => setFormData((val) => ({ ...val, street: e.target.value }))}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formDataError && formDataError.find((item) => item.item === 'street')?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={5}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    PROVINCE<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="province"
                                    options={address?.province}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={address?.province?.find(
                                        (option) => option.value?.toLowerCase() === formData.province?.toLowerCase()
                                    )}
                                    onChange={(selectedOption) => {
                                        filterCity(selectedOption);
                                    }}
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'province')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                />
                                {formDataError && formDataError.find((item) => item.item === 'province') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'province')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={5}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    MUNICIPALITY/CITY<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="city"
                                    id="city"
                                    options={address?.matchedCity}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={address?.matchedCity?.find(
                                        (option) => option.value?.toLowerCase() === formData.city?.toLowerCase()
                                    ) ||  address?.city?.find(
                                        (option) => option.value?.toLowerCase() === formData.city?.toLowerCase()
                                    )}
                                    onChange={(selectedOption) => {
                                        filterBarangay(selectedOption);
                                    }}
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'city')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                    style={{ zIndex: 0 }}
                                />
                                {formDataError && formDataError.find((item) => item.item === 'city') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'city')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={5}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    BARANGAY<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="barangay"
                                    options={address?.matchedBarangay}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={address?.matchedBarangay?.find(
                                        (option) => option.value?.toLowerCase() === formData.barangay?.toLowerCase()
                                    ) || address?.barangay?.find(
                                        (option) => option.value?.toLowerCase() === formData.barangay?.toLowerCase()
                                    )}
                                    onChange={(selectedOption) => {
                                        handleChange({ target: { name: 'barangay', value: selectedOption.value } });
                                        handleChange({ target: { name: 'zipCode', value: selectedOption.bmCode } });
                                    }}
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'barangay')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                />
                                {formDataError && formDataError.find((item) => item.item === 'barangay') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'barangay')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={2}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    ZIP CODE<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    // onChange={(e) => {
                                    //     if (!formData.zipCode) {
                                    //         setFormData((val) => ({ ...val, zipCode: e.target.value }));
                                    //     }
                                    // }}
                                    // disabled={formData?.zipCode}
                                    onChange={(e) => setFormData((val) => ({ ...val, zipCode: e.target.value }))}
                                    isInvalid={formDataError && !!formDataError.find((item) => item.item === 'zipCode')}
                                    readOnly
                                    style={{ background: '#E0E0E0' }}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formDataError && formDataError.find((item) => item.item === 'zipCode')?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    NATURE OF WORK<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="occupationClass"
                                    id="occupationClass"
                                    options={businessNatureOptions}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={businessNatureOptions.find(
                                        (option) => option.value === formData.occupationClass
                                    )}
                                    onChange={(selectedOption) =>
                                        setFormData((val) => ({ ...val, occupationClass: selectedOption.value }))
                                    }
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'occupationClass')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                    style={{ zIndex: 0 }}
                                />
                                {formDataError && formDataError.find((item) => item.item === 'occupationClass') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'occupationClass')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    NAME OF EMPLOYER<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="noe"
                                        value={formData.empBusName}
                                        onChange={(e) => setFormData((val) => ({ ...val, empBusName: e.target.value }))}
                                        isInvalid={
                                            formDataError && !!formDataError.find((item) => item.item === 'empBusName')
                                        }
                                        style={{ zIndex: 0 }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError &&
                                            formDataError.find((item) => item.item === 'empBusName')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={8}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    NATURE OF SELF-EMPLOYMENT/BUSINESS<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="noseb"
                                        value={formData.empBusNature}
                                        onChange={(e) =>
                                            setFormData((val) => ({ ...val, empBusNature: e.target.value }))
                                        }
                                        isInvalid={
                                            formDataError &&
                                            !!formDataError.find((item) => item.item === 'empBusNature')
                                        }
                                        style={{ zIndex: 0 }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formDataError &&
                                            formDataError.find((item) => item.item === 'empBusNature')?.message}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-group-label">
                                    SOURCE OF FUNDS<RequiredTag> *</RequiredTag>
                                </Form.Label>
                                <Select
                                    name="sourceFund"
                                    options={fundsOptions}
                                    getOptionLabel={(option) => option.value}
                                    getOptionValue={(option) => option.key}
                                    value={fundsOptions.find((option) => option.value === formData.sourceFund)}
                                    onChange={(selectedOption) =>
                                        setFormData((val) => ({ ...val, sourceFund: selectedOption.value }))
                                    }
                                    className={
                                        formDataError && formDataError.find((item) => item.item === 'sourceFund')
                                            ? 'is-invalid'
                                            : ''
                                    }
                                />
                                {formDataError && formDataError.find((item) => item.item === 'sourceFund') && (
                                    <div className="invalid-feedback">
                                        {formDataError.find((item) => item.item === 'sourceFund')?.message}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <StyledContainer2>
                        <Col>
                            <Form.Group xs={12} controlId="formCheckbox" style={{ color: 'rgba(41, 37, 97, 1)' }}>
                                <Form.Check
                                    className="checkbox mt-2"
                                    type="checkbox"
                                    onChange={handleCheckboxChange}
                                    name="agreed"
                                    label={
                                        <p>
                                            I have read, understood and agree to the{' '}
                                            <u>
                                                <a
                                                    href="https://bit.ly/HomeProtectTU"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ fontWeight: '600', color: '#292561' }}
                                                >
                                                    Terms of Use
                                                </a>
                                            </u>{' '}
                                            of this microsite, the{' '}
                                            <u>
                                                <a
                                                    href="https://bpims.com/privacy-policy/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ fontWeight: '600', color: '#292561' }}
                                                >
                                                    BPI MS Privacy Policy
                                                </a>
                                            </u>
                                            , the{' '}
                                            <u>
                                                <a
                                                    href="https://bpims.com/bpi-ms-stakeholder-charter/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ fontWeight: '600', color: '#292561' }}
                                                >
                                                    BPI MS Customer Charter
                                                </a>
                                            </u>{' '}
                                            and the{' '}
                                            <u>
                                                <a
                                                    href="https://bit.ly/HomeProtectionTC"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ fontWeight: '600', color: '#292561' }}
                                                >
                                                    Terms and Conditions
                                                </a>
                                            </u>{' '}
                                            of this Policy, issued and underwritten by BPI/MS Insurance Corporation for
                                            Home Credit Philippines.
                                        </p>
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </StyledContainer2>
                    <CenteredRow>
                        <StyledSubmitButton2 variant="secondary" type="submit">
                            Review Booking
                        </StyledSubmitButton2>
                    </CenteredRow>
                </StyledForm>
            </StyledContainer>
        </>
    );
}
